import { IAuthenticationEvent } from '../models/common-models';

/**
 * Decodes a JWT token and returns the complete payload
 * @param token - The JWT token string
 * @returns The decoded payload object or null if decoding fails
 */
export const decodeJWTPayload = (token: string): Record<string, unknown> | null => {
  try {
    if (!token || typeof token !== 'string') {
      return null;
    }

    // Split JWT into header.payload.signature
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }

    const payloadPart = parts[0];
    if (!payloadPart) {
      return null;
    }

    // Add padding if needed for base64 decoding
    const paddingNeeded = (4 - (payloadPart.length % 4)) % 4;
    const paddedPayload = payloadPart.padEnd(payloadPart.length + paddingNeeded, '=');

    // Decode base64 JWT payload
    const decoded = atob(paddedPayload);

    // Parse JSON payload
    const parsedPayload = JSON.parse(decoded);

    return parsedPayload;
  } catch (error) {
    return null;
  }
};

/**
 * Extracts a specific property from JWT payload
 * @param token - The JWT token string
 * @param property - The property to extract from the payload
 * @returns The value of the property or null if not found
 */
export const getJWTProperty = <T = unknown>(token: string, property: string): T | null => {
  try {
    const payload = decodeJWTPayload(token);
    if (!payload) {
      return null;
    }

    const result = payload[property] ?? null;

    return result as T | null;
  } catch (error) {
    return null;
  }
};
/**
 * Gets the 'v' property (timestamp) from JWT token payload
 * @param token - The JWT token to decode
 * @returns The timestamp value or null if not found
 */
export const getTimestampFromJWT = (token: string): string | null => {
  return getJWTProperty<string>(token, 'v');
};

export const shouldFetchAuthActivity = (
  jwt: string | null,
  isScanToPayEnabled: boolean,
  isUserInStore: boolean
): boolean => {
  const shouldFetch = !!(jwt && isScanToPayEnabled && isUserInStore);
  return shouldFetch;
};

export const processAccountActivityEvents = (
  allEvents: IAuthenticationEvent[],
  decodedJWT: string,
  authEventFilterHours: number,
  signInEventIDs: string[] = [],
  otpEventIDs: string[] = [],
  sessionId: string
): IAuthenticationEvent | null => {
  // Early return if no event IDs are configured
  const hasSignInEventIDs = signInEventIDs.length > 0;
  const hasOtpEventIDs = otpEventIDs.length > 0;

  if (!hasSignInEventIDs && !hasOtpEventIDs) {
    return null;
  }

  const now = new Date().getTime();
  const authEventCutoffTime = now - (authEventFilterHours * 60 * 60 * 1000);

  // Filter events within the configured time window
  const recentEvents = filterRecentEvents(allEvents, authEventCutoffTime);

  // Only filter for valid auth events if signInEventIDs are provided
  if (hasSignInEventIDs) {
    const validAuthEvents = filterValidAuthEvents(recentEvents, signInEventIDs);

    if (validAuthEvents.length > 0) {
      const match = findMatchingEvent(validAuthEvents, decodedJWT);
      if (match) {
        return match;
      }
    }
  }

  // Early return if no OTP event IDs are configured
  if (!hasOtpEventIDs) {
    return null;
  }

  const validOTPVerificationEvents = filterOTPVerificationEvents(recentEvents, otpEventIDs);

  if (validOTPVerificationEvents.length === 0) {
    return null;
  }

  const match = findMatchingSessionSignInEvent(validOTPVerificationEvents, sessionId);

  return match;
};

export const filterRecentEvents = (
  allEvents: IAuthenticationEvent[],
  authEventCutoffTime: number
): IAuthenticationEvent[] => {
  const firstOldEventIndex = allEvents.findIndex((event) => {
    if (event.date) {
      const eventTimestamp = new Date(event.date).getTime();
      return eventTimestamp < authEventCutoffTime;
    }
    return false;
  });

  return firstOldEventIndex === -1
    ? allEvents
    : allEvents.slice(0, firstOldEventIndex);
};

export const filterValidAuthEvents = (events: IAuthenticationEvent[], signInEventIDs: string[]):
IAuthenticationEvent[] => {
  return events.filter((event) => signInEventIDs.includes(event.eventId));
};

export const filterOTPVerificationEvents = (events: IAuthenticationEvent[], otpEventIDs: string[]):
IAuthenticationEvent[] => {
  return events.filter((event) => otpEventIDs.includes(event.eventId));
};

export const findMatchingEvent = (
  validAuthEvents: IAuthenticationEvent[],
  decodedJWT: string
): IAuthenticationEvent | null => {
  const jwtTimestamp = parseInt(decodedJWT, 10);

  const matchedEvent = validAuthEvents.find((event) => {
    if (!event.date) {
      return false;
    }
    const eventTimestamp = new Date(event.date).getTime();
    const timeDifference = Math.abs(jwtTimestamp - eventTimestamp);
    // Check if timestamps match within 1000ms tolerance
    return timeDifference <= 1000;
  });

  return matchedEvent ?? null;
};

export const findMatchingSessionSignInEvent = (
  validOTPVerificationEvents: IAuthenticationEvent[],
  sessionId: string
): IAuthenticationEvent | null => {
  // Start with the latest OTP verification event (first in array since sorted desc)
  return validOTPVerificationEvents.find(
    (event) => event.smSessionId === sessionId
  ) ?? null;
};
